import React from 'react';
import { Link } from 'react-router-dom';
import HeroTiles from '../components/HeroTiles';
import MailLink from '../components/MailLink';
import Reveal from '../components/Reveal';
import { SITE } from '../content/site';
import './Home.css';

const MARQUEE = [
  'Android',
  'iOS',
  'Flutter',
  'React Native',
  'Kotlin',
  'Swift',
  'On-device AI',
  'Store launch',
  'Ad monetisation',
  'Privacy by default',
];

const BUILD = [
  {
    k: '01',
    title: 'Consumer mobile apps',
    body: 'End-to-end product work: the idea sharpened into a flow, the flow into a build, the build onto the store. Android first, iOS where it earns its keep.',
    tags: ['Flutter', 'Kotlin', 'Swift'],
  },
  {
    k: '02',
    title: 'Tools people open daily',
    body: 'Scanners, timers, trackers, readers. Small surface, high frequency — the kind of app that survives a phone reset because someone would notice it missing.',
    tags: ['Offline-first', 'Widgets', 'Background work'],
  },
  {
    k: '03',
    title: 'AI-assisted features',
    body: 'Vision and language models wired into a real flow, with the on-device path taken wherever it keeps data on the phone and the bill at zero.',
    tags: ['Vision', 'LLM', 'On-device'],
  },
  {
    k: '04',
    title: 'Casual games',
    body: 'Short loops, honest difficulty, no dark patterns. Built to be finished in a queue and returned to tomorrow.',
    tags: ['Unity', 'Puzzle', 'Arcade'],
  },
  {
    k: '05',
    title: 'Monetisation that is not hostile',
    body: 'Ads placed outside the content, throttled by rules we write down, with a paid path that genuinely removes them. Revenue should not cost you the user.',
    tags: ['AdMob', 'Subscriptions', 'Consent'],
  },
  {
    k: '06',
    title: 'Launch and compliance',
    body: 'Store listings, data safety forms, policy reviews, target-API deadlines. The unglamorous half of shipping, handled before it becomes an email from Google.',
    tags: ['ASO', 'Data safety', 'Reviews'],
  },
];

const PROCESS = [
  {
    step: 'Scope',
    body: 'One page: who opens this, on which screen, and what they get in the first thirty seconds. Anything that does not serve that is parked, not built.',
  },
  {
    step: 'Prototype',
    body: 'A running build on a real device inside the first week. Opinions about a flow are cheap; a thumb on a screen settles them.',
  },
  {
    step: 'Build',
    body: 'Short cycles, each one ending in something installable. Performance, offline behaviour and permissions are handled as they appear, not in a cleanup sprint that never comes.',
  },
  {
    step: 'Ship and tend',
    body: 'Store submission, the policy paperwork, then the part most studios skip: watching crash rates, review text and retention, and fixing what they say.',
  },
];

const PRINCIPLES = [
  {
    title: 'Collect the minimum',
    body: 'If a feature works without an account, it ships without one. Data that never leaves the phone cannot leak, cannot be subpoenaed and cannot be sold.',
  },
  {
    title: 'Say it in plain words',
    body: 'Our policies name what is collected, why, and for how long — in sentences a person can read once. No clause exists to be skimmed past.',
  },
  {
    title: 'Deletion that works',
    body: 'One page, one request, done inside thirty days and usually the same day. No support maze, no retention offer, no exceptions.',
  },
];

function Home() {
  return (
    <main id="main">
      {/* ------------------------------------------------------------ hero */}
      <header className="hero">
        <div className="hero-wash" aria-hidden="true" />
        <div className="wrap hero-in">
          <div className="hero-copy">
            <span className="pill">
              <i className="dot" />
              Independent app studio · since {SITE.since}
            </span>
            <h1 className="hero-title">
              We build apps that <span className="marker">earn their place</span>{' '}
              on the home screen.
            </h1>
            <p className="hero-sub">
              AppNexa is a small mobile studio. We take an idea from a one-page
              scope to a signed release on the store — and we write our privacy
              policy, terms and deletion process in language you can actually
              read.
            </p>
            <div className="hero-cta">
              <Link className="btn" to="/contact">
                Start a project
              </Link>
              <Link className="btn ghost" to="/privacy">
                Read our privacy policy
              </Link>
            </div>
            <ul className="hero-facts">
              <li>
                <strong>Android &amp; iOS</strong>
                <span>Flutter, Kotlin, Swift</span>
              </li>
              <li>
                <strong>Store-ready</strong>
                <span>listings, data safety, policy reviews</span>
              </li>
              <li>
                <strong>Private by default</strong>
                <span>on-device first, deletion in one page</span>
              </li>
            </ul>
          </div>

          <HeroTiles />
        </div>

        <div className="ticker" aria-hidden="true">
          <div className="ticker-track">
            {[...MARQUEE, ...MARQUEE].map((item, i) => (
              <span key={`${item}-${i}`}>
                {item}
                <i>●</i>
              </span>
            ))}
          </div>
        </div>
      </header>

      {/* ------------------------------------------------------------ work */}
      <section id="work">
        <div className="wrap">
          <Reveal>
            <span className="eyebrow">What we build</span>
            <h2 className="section-title">
              Six kinds of work, one <span className="gradient-text">standard</span>.
            </h2>
            <p className="section-lede">
              Most of what we ship is small, used daily, and lives on a phone in
              someone&apos;s pocket. The constraint is the point: a tool with one
              job has nowhere to hide.
            </p>
          </Reveal>

          <div className="grid-3">
            {BUILD.map((item, i) => (
              <Reveal key={item.k} delay={i * 70} className="build-card card-surface">
                <span className="build-k">{item.k}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
                <div className="tags">
                  {item.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------- process */}
      <section id="process" className="process">
        <div className="wrap">
          <Reveal>
            <span className="eyebrow">How we work</span>
            <h2 className="section-title">
              Four steps, and none of them is{' '}
              <span className="gradient-text">a surprise</span>.
            </h2>
          </Reveal>

          <ol className="steps">
            {PROCESS.map((item, i) => (
              <Reveal as="li" key={item.step} delay={i * 90} className="step">
                <div className="step-n">{String(i + 1).padStart(2, '0')}</div>
                <div className="step-body">
                  <h3>{item.step}</h3>
                  <p>{item.body}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* --------------------------------------------------- privacy-first */}
      <section id="privacy-first" className="privacy-first">
        <div className="wrap">
          <div className="pf-grid">
            <Reveal>
              <span className="eyebrow">Privacy first</span>
              <h2 className="section-title">
                The boring page we{' '}
                <span className="gradient-text">take seriously</span>.
              </h2>
              <p className="section-lede">
                Every app we publish carries ads or a paid tier, so data is worth
                money to someone. That is exactly why the rules below are written
                down rather than implied.
              </p>
              <div className="pf-links">
                <Link to="/privacy" className="pf-link card-surface">
                  <span>Privacy policy</span>
                  <i>→</i>
                </Link>
                <Link to="/terms" className="pf-link card-surface">
                  <span>Terms of service</span>
                  <i>→</i>
                </Link>
                <Link to="/delete-account" className="pf-link card-surface">
                  <span>Delete your account</span>
                  <i>→</i>
                </Link>
              </div>
            </Reveal>

            <div className="pf-cards">
              {PRINCIPLES.map((item, i) => (
                <Reveal key={item.title} delay={i * 90} className="pf-card card-surface">
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- cta */}
      <section className="cta band-dark">
        <div className="wrap">
          <Reveal className="cta-box">
            <h2 className="section-title">
              Got something worth <span className="gradient-text">building</span>?
            </h2>
            <p className="section-lede">
              Tell us what it does on the first screen. If it is a fit we will say
              so within two working days, and if it is not we will say that too.
            </p>
            <div className="hero-cta">
              <Link className="btn on-dark ghost" to="/contact">
                Start a project
              </Link>
              <MailLink className="btn" subject="Project enquiry">
                Email the studio
              </MailLink>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default Home;
